import React from 'react'
import { Formik, Form } from 'formik'
import { addDays, isWithinInterval, parseISO } from 'date-fns'
import { cloneDeep } from 'lodash'
import { Button, Element } from 'react-bulma-components'
import { Mission } from '../../../../api/missions'
import type { MissionFormValues } from '../../../missions/mission-form'
import { WorkPeriodsEditor } from '../../work-periods-editor/work-periods-editor'
import type { WorkPeriodEditorFormErrors } from '../../work-periods-editor/work-period-editor'
import { RequestButton, RequestMessage } from '../../../request-components/request-components'
import { useUpdateWorkPeriodsMutation } from '../../../../queries/missions'
import { IsoDate } from '../../../../hooks/use-calendar'
import { getDocIdAsString } from '../../../../utils/documents'
import { InlineWorkPeriodsFormValues, validateInlineWorkPeriodsForm } from './utils'
import { localDate } from '../../../../utils/date'

export interface InlineWorkPeriodsFormProps {
  mission: Mission
  weekRangeFromIso: IsoDate | ''
  weekRangeToIso: IsoDate | ''
  onCancel: () => void
  onSuccess: () => void
}

/**
 * Formik wrapper around WorkPeriodsEditor for inline theoretical hours editing on mission details.
 * Persists via PUT /missions/:id/work-periods (bulk work period diff, optional range).
 */
const InlineWorkPeriodsForm: React.FC<InlineWorkPeriodsFormProps> = ({
  mission,
  weekRangeFromIso,
  weekRangeToIso,
  onCancel,
  onSuccess,
}) => {
  const updateWorkPeriodsMutation = useUpdateWorkPeriodsMutation()

  /** Snapshot of server work periods for WorkPeriodEditor "initial" / cancel (same mapping as mission-form). */
  const initialWorkPeriodsForEditor = React.useMemo(
    () =>
      mission.workPeriods.map(wP => ({
        ...wP,
        start: { date: new Date(wP.start.date) },
        end: { date: new Date(wP.end.date) },
      })),
    [mission],
  )

  const formInitialValues = React.useMemo((): InlineWorkPeriodsFormValues => {
    return {
      meta: {
        startDate: weekRangeFromIso || '',
        endDate: weekRangeToIso || '',
      },
      workPeriods: initialWorkPeriodsForEditor,
    }
  }, [weekRangeFromIso, weekRangeToIso, initialWorkPeriodsForEditor])

  const rangeStart = React.useMemo(() => parseISO(weekRangeFromIso), [weekRangeFromIso])
  const rangeEnd = React.useMemo(() => addDays(parseISO(weekRangeToIso), 1), [weekRangeToIso])

  const handleSubmitInline = (values: InlineWorkPeriodsFormValues): void => {
    const rangeInterval = { start: rangeStart, end: rangeEnd }

    const editedInRange = cloneDeep(
      values.workPeriods as Array<Required<MissionFormValues['workPeriods'][0]>>,
    )
      .filter(wP => isWithinInterval(wP.start.date, rangeInterval))
      .map(wP => ({ start: { date: wP.start.date }, end: { date: wP.end.date } }))

    updateWorkPeriodsMutation.mutate(
      {
        missionId: mission._id,
        workPeriods: editedInRange,
        range: { start: rangeStart, end: rangeEnd },
      },
      {
        onSuccess: () => onSuccess(),
      },
    )
  }

  return (
    <Formik<InlineWorkPeriodsFormValues>
      key={`${mission._id}-${weekRangeFromIso}-${weekRangeToIso}`}
      enableReinitialize
      initialValues={formInitialValues}
      validate={validateInlineWorkPeriodsForm}
      onSubmit={handleSubmitInline}
    >
      {({ values, setFieldValue, errors, resetForm }) => (
        <Form>
          <WorkPeriodsEditor
            workPeriods={values.workPeriods}
            jobTitle={getDocIdAsString(mission.jobTitle)}
            initialWorkPeriods={initialWorkPeriodsForEditor}
            resource={mission}
            user={getDocIdAsString(mission.user)}
            setWorkPeriods={wPs => setFieldValue('workPeriods', wPs)}
            errors={errors as WorkPeriodEditorFormErrors}
            startDate={values.meta.startDate}
            endDate={values.meta.endDate}
            hideDateRange
          />
          <>
            <Element>
              <RequestButton
                type="submit"
                color="primary"
                mutation={updateWorkPeriodsMutation}
                data-test="mission-work-periods-inline-save"
              >
                Sauvegarder
              </RequestButton>
              <RequestMessage mutation={updateWorkPeriodsMutation} />
              <Button
                type="button"
                className="ml-3"
                onClick={() => {
                  resetForm()
                  onCancel()
                }}
              >
                Annuler
              </Button>
            </Element>
            <Element textColor="grey" mt={3} renderAs="p">
              Uniquement les journées de la période sélectionnée (du {localDate(weekRangeFromIso)}{' '}
              au {localDate(weekRangeToIso)}) seront modidifées.
              <br /> Les journées hors de la sélection ne seront pas impactées.
            </Element>
          </>
        </Form>
      )}
    </Formik>
  )
}

export default InlineWorkPeriodsForm
